import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box, Text, VStack, Spinner, Center } from "@chakra-ui/react";
import {
  CollectionPage,
  type Column,
  type Facets,
  type Keyable,
  type Item,
  HelixLink,
  useCollection,
} from "@sciencecorp/helix-components";
import {
  allVisionConditions,
  allVisionConditionLabels,
} from "@/api/application/application";
import { useAdminApplications, type AdminApplication } from "@/api/admin/applications";
import { formatDate } from "@/utils/dateFormat";
import {
  getCountryName,
  formatStatus,
  calculateAge,
  formatApplicationType,
} from "./utils";
import { AdminHeader } from "./AdminHeader";

// Vision questions ordered from best to worst eyesight
const VISION_QUESTIONS = [
  { key: "vision_can_read_normal_print", label: "Normal print" },
  { key: "vision_can_read_headlines", label: "Headlines" },
  { key: "vision_can_see_indoors", label: "Room light" },
  { key: "vision_can_localize_person", label: "Localize person" },
] as const;

// Helper function to get compressed vision status
const getCompressedVisionStatus = (
  visionAcuityKnown: boolean | null,
  visionAcuityLeft: string | null,
  visionAcuityRight: string | null,
  country: string | null,
  visionCanReadNormalPrint: boolean | null,
  visionCanReadHeadlines: boolean | null,
  visionCanSeeIndoors: boolean | null,
  visionCanLocalizePerson: boolean | null,
): React.ReactNode => {
  if (visionAcuityKnown === true && visionAcuityLeft && visionAcuityRight) {
    const leftValue = visionAcuityLeft;
    const rightValue = visionAcuityRight;

    switch (country) {
      case "DE":
        return (
          <>
            L: {leftValue}%<br />
            R: {rightValue}%
          </>
        );
      case "FR":
        return (
          <>
            L: {leftValue}
            <br />
            R: {rightValue}
          </>
        );
      case "GB":
        return (
          <>
            L: 6/{leftValue}
            <br />
            R: 6/{rightValue}
          </>
        );
      default:
        return (
          <>
            L: 20/{leftValue}
            <br />
            R: 20/{rightValue}
          </>
        );
    }
  }

  if (visionAcuityKnown === false) {
    const answers = [
      { value: visionCanReadNormalPrint, question: VISION_QUESTIONS[0] },
      { value: visionCanReadHeadlines, question: VISION_QUESTIONS[1] },
      { value: visionCanSeeIndoors, question: VISION_QUESTIONS[2] },
      { value: visionCanLocalizePerson, question: VISION_QUESTIONS[3] },
    ];

    let bestVisionIndex = -1;
    for (let i = 0; i < answers.length; i++) {
      if (answers[i].value === true) {
        bestVisionIndex = i;
        break;
      }
    }

    return (
      <VStack spacing={0} align="stretch">
        {answers.map((answer, index) => {
          const isBestVision = index === bestVisionIndex;
          let answerText: string;
          let answerColor: string;

          if (answer.value === true) {
            answerText = "Yes";
            answerColor = "green.600";
          } else if (answer.value === false) {
            answerText = "No";
            answerColor = "red.600";
          } else {
            answerText = "—";
            answerColor = "gray.500";
          }

          return (
            <Text
              key={answer.question.key}
              fontWeight={isBestVision ? "bold" : "normal"}
              margin={0}
            >
              {answer.question.label}:{" "}
              <Text as="span" color={answerColor}>
                {answerText}
              </Text>
            </Text>
          );
        })}
      </VStack>
    );
  }

  return "—";
};

// Application item type for the collection (extends AdminApplication with Keyable)
type ApplicationItem = AdminApplication & Keyable;

// Helper to format conditions with AMD type if applicable
const formatConditions = (conditions: string[], amdType: string | null): React.ReactNode => {
  if (!conditions || conditions.length === 0) {
    return <Text>—</Text>;
  }

  const formattedConditions = conditions.map((condition) => {
    if (condition === "AGE_RELATED_MACULAR_DEGENERATION" && amdType) {
      const capitalizedType = amdType.charAt(0).toUpperCase() + amdType.slice(1);
      return (
        <Text fontWeight={capitalizedType === "Dry" || capitalizedType === "Both" ? "bold" : ""}>AMD ({capitalizedType})</Text>
      );
    }
    const label =
      allVisionConditionLabels[condition as keyof typeof allVisionConditionLabels] || condition;
    return label;
  });

  return (
    <VStack spacing={0} alignItems="flex-start">
      {formattedConditions.map((condition, index) => (
        <Text key={index} margin={0}>
          {condition}
        </Text>
      ))}
    </VStack>
  );
};

// Country to display for an application in the admin list. Falls back to
// proxy_country when patient_country is null or blank (pre-validation legacy
// data); mirrors DISPLAY_COUNTRY_SQL.
// Not interchangeable with patient_country in general — patient and proxy can
// live in different countries. Only use for display/sort in admin.
const getAdminCountry = (item: AdminApplication): string | null =>
  item.patient_country || item.proxy_country || null;

// --- Facet option lists + label renderers ---

const STATUS_OPTIONS = ["pending", "approved", "rejected"] as const;
const CONDITION_OPTIONS = Object.values(allVisionConditions);
// "none" matches apps with NULL vision_macular_degeneration_type — i.e. apps
// without AMD (or with AMD but no type recorded). Server-side scope handles
// the SQL nullability.
const AMD_TYPE_OPTIONS = ["dry", "wet", "both", "none"] as const;
const YES_NO_OPTIONS = ["yes", "no"] as const;
// Acuity Meets PRIMA is tri-state — see ACUITY_MEETS_PRIMA_SQL in
// app/models/registry/application.rb. "unknown" surfaces apps where we lack
// the data to make a definitive yes/no determination.
const ACUITY_MATCH_OPTIONS = ["yes", "no", "unknown"] as const;

const renderConditionLabel = (value: unknown): string => {
  const conditionValue = String(value);
  let label =
    allVisionConditionLabels[conditionValue as keyof typeof allVisionConditionLabels] ||
    conditionValue;
  if (label === "Age-related macular degeneration") label = "AMD";
  return label;
};
const renderAmdType = (value: unknown): string => {
  const s = String(value);
  if (s === "none") return "N/A";
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const renderYesNo = (value: unknown): string => (value === "yes" ? "Yes" : "No");
const renderAcuityMatch = (value: unknown): string => {
  switch (String(value)) {
    case "yes":
      return "Yes";
    case "no":
      return "No";
    case "unknown":
      return "Unknown";
    default:
      return String(value);
  }
};
const renderCountry = (value: unknown): string => getCountryName(String(value));

// helix-components' Facets sidebar sorts entries alphabetically by key, with no
// prop to override (see node_modules/.../helix-components.es.js line ~4425).
// We keep the canonical filter keys (used in URL params and API calls) but
// prefix them with letters when handing facets to CollectionPage so the
// alphabetical sort lands on our intended order. The prefix is stripped in
// `handleFacets` before bubbling up to useCollection, so URLs stay clean.
const FACET_ORDER = [
  "status",
  "condition",
  "amd_type",
  "acuity_meets_prima",
  "country",
  "can_read_headlines",
  "travel_willing",
] as const;
type FacetKey = (typeof FACET_ORDER)[number];

const displayKey = (key: FacetKey): string =>
  `${String.fromCharCode(97 + FACET_ORDER.indexOf(key))}_${key}`;
// Only strip the alphabet range we actually emit in `displayKey`, so a future
// canonical facet key like `e_signature` isn't silently mangled.
const DISPLAY_PREFIX_REGEX = new RegExp(
  `^[a-${String.fromCharCode(96 + FACET_ORDER.length)}]_`,
);
const stripDisplayPrefix = (key: string): string => key.replace(DISPLAY_PREFIX_REGEX, "");

// Reviewer-facing definition of the "Acuity Meets PRIMA" filter.
//
// This text MUST stay in sync with two source-of-truth definitions:
//   1. `primaEligible` in app/javascript/components/Registry/pages/ConfirmPage.tsx
//      (the rule that fires registration_qual / registration_un to Plausible)
//   2. `ACUITY_MEETS_PRIMA_SQL` in app/models/registry/application.rb
//      (the SQL the admin filter actually uses)
// If you change the rule in either of those, update this text too — clinicians
// review this box to audit what the system is doing.
const AcuityMeetsPrimaDefinition = () => (
  <Box as="details" marginTop={10} fontSize="sm">
    <Box as="summary" cursor="pointer" fontWeight="medium">
      What does &ldquo;Acuity Meets PRIMA&rdquo; mean?
    </Box>
    <VStack align="stretch" spacing={3} marginTop={3}>
      <Text>
        An application is classified into one of three states based on its vision data:
      </Text>
      <Box as="ul" paddingInlineStart={5}>
        <Text as="li" marginBottom={1}>
          <strong>Yes</strong> — data clearly indicates the patient passes the acuity gate:
          <Box as="ul" paddingInlineStart={5} marginTop={1}>
            <Text as="li">Acuity known with valid numeric values that meet the country threshold:</Text>
            <Box as="ul" paddingInlineStart={5}>
              <Text as="li">Germany (DE): worse eye ≤ 6%, better eye ≤ 10%</Text>
              <Text as="li">France (FR): worse eye ≤ 0.06, better eye ≤ 0.1</Text>
              <Text as="li">UK (GB): worse eye ≥ 6/96, better eye ≥ 6/60</Text>
              <Text as="li">US / default: worse eye ≥ 20/320, better eye ≥ 20/200</Text>
            </Box>
            <Text as="li">
              <em>or</em> acuity not known + both functional answers recorded as &ldquo;no&rdquo;
              (cannot read normal print <em>and</em> cannot read headlines).
            </Text>
          </Box>
        </Text>
        <Text as="li" marginBottom={1}>
          <strong>No</strong> — data clearly indicates the patient fails the acuity gate:
          <Box as="ul" paddingInlineStart={5} marginTop={1}>
            <Text as="li">Acuity known with valid values that do not meet the country threshold,</Text>
            <Text as="li">
              <em>or</em> acuity not known + at least one functional answer is &ldquo;yes&rdquo;
              (vision too good to qualify).
            </Text>
          </Box>
        </Text>
        <Text as="li" marginBottom={1}>
          <strong>Unknown</strong> — insufficient data to evaluate:
          <Box as="ul" paddingInlineStart={5} marginTop={1}>
            <Text as="li">Acuity-known status is unset, or</Text>
            <Text as="li">Acuity is known but the numeric values are missing or invalid, or</Text>
            <Text as="li">
              Acuity is not known and at least one functional answer is missing.
            </Text>
          </Box>
        </Text>
      </Box>
    </VStack>
  </Box>
);

export const Admin = () => {
  const location = useLocation();
  const { filters, pagination, order, onFacets, onPagination, onOrder } = useCollection({
    order: { submitted_at: "desc" },
    pagination: { per_page: 10 },
  });

  // Persist the current admin URL so PatientDetail's back button can restore
  // filters/sort/page even when arriving via direct link or external referrer.
  useEffect(() => {
    sessionStorage.setItem("admin_last_url", location.pathname + location.search);
  }, [location.pathname, location.search]);

  const { data, isLoading, error } = useAdminApplications({
    filters,
    page: pagination.page,
    per_page: pagination.per_page,
    order,
  });

  // Attach `key` so CollectionPage can treat each row as Keyable.
  const applications: ApplicationItem[] = useMemo(() => {
    if (!data?.applications) return [];
    return data.applications.map((app) => ({ ...app, key: app.id }));
  }, [data?.applications]);

  // Build display facets from static option lists + URL-selected state (via
  // useCollection's `filters`) + server counts.
  const displayFacets = useMemo<Facets>(() => {
    const counts = data?.facet_counts;
    const isSelected = (facetKey: string, value: string) =>
      (filters[facetKey] as string[] | undefined)?.includes(value) ?? false;
    const buildOptions = (
      values: readonly string[],
      facetKey: string,
      facetCounts: Record<string, number> | undefined,
    ) =>
      values.map((v) => ({
        value: v,
        key: v,
        count: facetCounts?.[v] ?? 0,
        selected: isSelected(facetKey, v),
      }));

    // Country options come from whatever countries the server returned counts
    // for, plus any country the URL is filtering on (so it stays visible even
    // if it has zero results given other filters). Sorted alphabetically by
    // display name so the list is scannable.
    const selectedCountries = (filters.country as string[] | undefined) ?? [];
    const countryOptions = Array.from(
      new Set([...Object.keys(counts?.country ?? {}), ...selectedCountries]),
    ).sort((a, b) => getCountryName(a).localeCompare(getCountryName(b)));
    return {
      [displayKey("status")]: {
        label: "Status",
        options: buildOptions(STATUS_OPTIONS, "status", counts?.status),
      },
      [displayKey("condition")]: {
        label: "Condition",
        render: renderConditionLabel,
        options: buildOptions(CONDITION_OPTIONS, "condition", counts?.condition),
      },
      [displayKey("amd_type")]: {
        label: "AMD Type",
        render: renderAmdType,
        options: buildOptions(AMD_TYPE_OPTIONS, "amd_type", counts?.amd_type),
      },
      [displayKey("acuity_meets_prima")]: {
        label: "Acuity Meets PRIMA",
        render: renderAcuityMatch,
        options: buildOptions(
          ACUITY_MATCH_OPTIONS,
          "acuity_meets_prima",
          counts?.acuity_meets_prima,
        ),
      },
      [displayKey("country")]: {
        label: "Country",
        render: renderCountry,
        options: buildOptions(countryOptions, "country", counts?.country),
      },
      [displayKey("can_read_headlines")]: {
        label: "Can Read Headlines",
        render: renderYesNo,
        options: buildOptions(YES_NO_OPTIONS, "can_read_headlines", counts?.can_read_headlines),
      },
      [displayKey("travel_willing")]: {
        label: "Willing to Travel",
        render: renderYesNo,
        options: buildOptions(YES_NO_OPTIONS, "travel_willing", counts?.travel_willing),
      },
    };
  }, [filters, data?.facet_counts]);

  // Strip the display-only ordering prefix from facet keys before bubbling up
  // to useCollection, so URL params and API calls use canonical names.
  const handleFacets = useCallback(
    (next: Facets) => {
      const cleaned: Facets = {};
      for (const [key, facet] of Object.entries(next)) {
        cleaned[stripDisplayPrefix(key)] = facet;
      }
      onFacets(cleaned);
    },
    [onFacets],
  );

  const columns: Column<ApplicationItem>[] = useMemo(
    () => [
      {
        label: "ID",
        render: (item: Item<ApplicationItem>) => (
          <HelixLink
            model="application"
            id={item.id}
            identifier={item.id.slice(0, 8)}
            disablePopover
            modelName="Application"
            size="small"
            navigateTo={`/admin/patient/${item.id}`}
          />
        ),
      },
      {
        label: "Status",
        render: (item: Item<ApplicationItem>) => {
          const { text, color } = formatStatus(item.status);
          return <Text color={color}>{text}</Text>;
        },
      },
      {
        label: "Type",
        render: (item: Item<ApplicationItem>) => (
          <Text>{formatApplicationType(item.applicant_type)}</Text>
        ),
      },
      {
        label: "Visual Acuity",
        render: (item: Item<ApplicationItem>) => (
          <Text>
            {getCompressedVisionStatus(
              item.vision_acuity_known,
              item.vision_acuity_left,
              item.vision_acuity_right,
              getAdminCountry(item),
              item.vision_can_read_normal_print,
              item.vision_can_read_headlines,
              item.vision_can_see_indoors,
              item.vision_can_localize_person,
            )}
          </Text>
        ),
      },
      {
        label: "Patient Country",
        render: (item: Item<ApplicationItem>) => (
          <Text>{getCountryName(getAdminCountry(item))}</Text>
        ),
      },
      {
        label: "Conditions",
        render: (item: Item<ApplicationItem>) =>
          formatConditions(item.vision_conditions, item.vision_macular_degeneration_type),
      },
      {
        label: "Age",
        orderOptions: { orderKey: "patient_date_of_birth" },
        render: (item: Item<ApplicationItem>) => (
          <Text>{calculateAge(item.patient_date_of_birth)}</Text>
        ),
      },
      {
        label: "Date Submitted",
        orderOptions: { orderKey: "submitted_at" },
        render: (item: Item<ApplicationItem>) => <Text>{formatDate(item.submitted_at)}</Text>,
      },
    ],
    [],
  );

  if (isLoading && !data) {
    return (
      <Center py={20}>
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={20}>
        <Text color="red.500">
          Error loading applications: {error instanceof Error ? error.message : "Unknown error"}
        </Text>
      </Center>
    );
  }

  const serverPagination = data?.pagination;

  return (
    <>
      <AdminHeader stats={data?.stats} />
      <CollectionPage
        title="Applications"
        columns={columns}
        isLoading={isLoading}
        items={applications}
        facets={displayFacets}
        facetsProps={{
          defaultIndex: [0],
        }}
        pagination={{
          page: serverPagination?.page ?? pagination.page,
          per_page: serverPagination?.per_page ?? pagination.per_page,
          n_pages: serverPagination?.n_pages ?? 0,
          total: serverPagination?.total ?? 0,
        }}
        order={order}
        onFacets={handleFacets}
        onPagination={onPagination}
        onOrder={onOrder}
      />
      <AcuityMeetsPrimaDefinition />
    </>
  );
};
